import { useEffect, useCallback } from "react";
import { useQAContext } from "@/contexts/useQAContext";

/**
 * Hook to manage QA draft persistence
 * Auto-saves QA state to localStorage and restores on mount
 */
export function useQADraftManager(
	reflectionId: string | null,
	isDraft: boolean,
) {
	const {
		setQaHistory,
		setConversationHistory,
		setCurrentQuestion,
		setCurrentAnswer,
	} = useQAContext();

	// Generate a unique key for this reflection's draft
	const getDraftKey = useCallback(() => {
		return reflectionId ? `qa_draft_${reflectionId}` : "qa_draft_new";
	}, [reflectionId]);

	// Save QA draft to localStorage
	const saveDraft = useCallback(
		(data: {
			qaHistory: Array<{ question: string; answer: string }>;
			conversationHistory: Array<{
				role: "user" | "assistant";
				content: string;
			}>;
			currentQuestion: string;
			currentAnswer: string;
		}) => {
			try {
				const key = getDraftKey();
				localStorage.setItem(key, JSON.stringify(data));
			} catch (error) {
				console.error("Failed to save QA draft:", error);
			}
		},
		[getDraftKey],
	);

	// Restore QA draft from localStorage
	const restoreDraft = useCallback(() => {
		try {
			const key = getDraftKey();
			const stored = localStorage.getItem(key);
			if (stored) {
				const data = JSON.parse(stored);
				if (data.qaHistory && Array.isArray(data.qaHistory)) {
					setQaHistory(data.qaHistory);
				}
				if (
					data.conversationHistory &&
					Array.isArray(data.conversationHistory)
				) {
					setConversationHistory(data.conversationHistory);
				}
				if (data.currentQuestion) {
					setCurrentQuestion(data.currentQuestion);
				}
				if (data.currentAnswer) {
					setCurrentAnswer(data.currentAnswer);
				}
				return true;
			}
		} catch (error) {
			console.error("Failed to restore QA draft:", error);
		}
		return false;
	}, [
		getDraftKey,
		setQaHistory,
		setConversationHistory,
		setCurrentQuestion,
		setCurrentAnswer,
	]);

	// Clear draft from localStorage
	const clearDraft = useCallback(() => {
		try {
			const key = getDraftKey();
			localStorage.removeItem(key);
		} catch (error) {
			console.error("Failed to clear QA draft:", error);
		}
	}, [getDraftKey]);

	// Restore on mount if this is a draft reflection
	useEffect(() => {
		if (isDraft && reflectionId) {
			restoreDraft();
		}
	}, [isDraft, reflectionId, restoreDraft]);

	return {
		saveDraft,
		restoreDraft,
		clearDraft,
	};
}
